import { useInstanceSettingsStore } from "#src/stores/instance-settings.js";
import { HttpBootService } from "#src/services/http-boot.service.js";
import { CRITICAL_FAILURE_REASONS } from "#src/stores/step-critical-failure.js";
import { ApprovedDomain } from "#src/structures/ApprovedDomain.js";
import { OverridePrefillBehavior } from "#src/structures/OverridePrefillBehavior.js";
import { parseErrorMessage } from "#src/util/helpers.js";

export class InstanceService {
  #pinia;
  #httpBootService;
  constructor(pinia) {
    this.#pinia = pinia;
    this.#httpBootService = new HttpBootService(pinia);
  }

  initialize() {
    const instance = useInstanceSettingsStore(this.#pinia);
    instance.setDefaults();
  }

  storePrefillData(params) {
    const instance = useInstanceSettingsStore(this.#pinia);
    instance.prefill.location = params;
  }

  setApiKey(apiKey) {
    const instance = useInstanceSettingsStore(this.#pinia);
    instance.api_key = apiKey;
  }

  getDomainKey() {
    const instance = useInstanceSettingsStore(this.#pinia);
    return instance.domain_key;
  }

  getTheme() {
    const instance = useInstanceSettingsStore(this.#pinia);
    return instance.theme.type;
  }

  async setQuoteAndApplyContext({
    fullUrl,
    slug,
    query,
    isExternallyEmbedded = false,
    isApi = false,
  } = {}) {
    const instance = useInstanceSettingsStore(this.#pinia);
    if (slug && !slug.startsWith("/?") && !isExternallyEmbedded)
      instance.app_slug = slug;
    instance.isExternallyEmbedded = Boolean(isExternallyEmbedded);
    this.#setPrefillData(query, fullUrl);

    const result = await this.#getQuoteAndApplyContext(fullUrl, isApi);
    let approvedDomain = result?.approvedDomain;
    if (!approvedDomain) approvedDomain = ApprovedDomain();
    let overridePrefillBehavior = result?.overridePrefillBehavior;
    if (!overridePrefillBehavior)
      overridePrefillBehavior = OverridePrefillBehavior();
    instance.$patch({
      ...approvedDomain,
      overridePrefillBehavior,
      loaded_approved_domain: true,
      isDarkMode: Boolean(
        instance.queryPrefillForValue(v => v?.dark === "true")
      ),
    });

    if (approvedDomain.error_message?.text) {
      throw { criticalFailureReason: CRITICAL_FAILURE_REASONS.SERVER_BLOCKED };
    }

    if (approvedDomain.requires_subscription && isExternallyEmbedded) {
      throw {
        criticalFailureReason: CRITICAL_FAILURE_REASONS.REQUIRES_SUBSCRIPTION,
      };
    }

    return instance.loaded_approved_domain;
  }

  async #getQuoteAndApplyContext(fullUrl, isApi = false) {
    const instance = useInstanceSettingsStore(this.#pinia);

    const isSignupMode = instance.queryPrefillForValue(
      v => v?.mode === "signup"
    );

    const bootData = this.#getBootData(
      instance.prefill,
      instance.app_slug ? fullUrl : null
    );
    const hasBootData = Object.values(bootData).length;
    if (!hasBootData && (instance.referData || isSignupMode)) return;
    else if (!hasBootData)
      throw { criticalFailureReason: CRITICAL_FAILURE_REASONS.BAD_DOMAIN };

    try {
      const result = await this.#httpBootService.getInstanceConfig(bootData);
      return result;
    } catch (e) {
      // The reason why we have bootData.npn && isApi check is
      // Some integrations, like EZLynx and Vertafore, have a "simple" integration.
      // These pass an NPN in and Quote & Apply is responsible for handling it regardless
      // if the account exists or not.
      //
      // Having an API check here will result in API customers getting better feedback.
      if (bootData.domain_key || bootData.url || (bootData.npn && isApi)) {
        throw {
          criticalFailureReason: CRITICAL_FAILURE_REASONS.BAD_DOMAIN,
          message: parseErrorMessage(e),
        };
      }
    }
  }

  #getBootData(prefill, fullUrl) {
    const appendData = (prefill, data) => {
      if (prefill?.npn) data.npn = prefill.npn;
      if (prefill?.client_id) data.domain_key = prefill.client_id;
      if (prefill?.eapp_id && prefill.eapp_uuid) {
        data.with_archived = "true";
        data.eapp_id = prefill.eapp_id;
        data.eapp_uuid = prefill.eapp_uuid;
      }
      if (prefill?.case_id && prefill.case_uuid) {
        data.with_archived = "true";
        data.case_id = prefill.case_id;
        data.case_uuid = prefill.case_uuid;
      }
      if (prefill?.referrer_id && prefill.referrer_type) {
        data.referrer_id = prefill.referrer_id;
        data.referrer_type = prefill.referrer_type;
      }
      if (prefill?.referral_uid) {
        data.referral_uid = prefill.referral_uid;
      }

      return data;
    };

    const bootData = {};
    if (fullUrl) bootData.url = fullUrl;
    appendData(prefill.location, bootData);
    appendData(prefill.parent, bootData);
    appendData(prefill.script, bootData);

    if (!bootData.url && !bootData.domain_key && !("npn" in bootData)) {
      delete bootData.with_archived;
    }

    return bootData;
  }

  #prefillToObject(prefill) {
    const getParams = url => {
      try {
        const urlObj = new URL(url);
        return Object.fromEntries(urlObj.searchParams);
        // eslint-disable-next-line no-unused-vars
      } catch (e) {
        return {};
      }
    };

    let parent = null;
    if (prefill?.parent_url) {
      parent = getParams(decodeURIComponent(prefill.parent_url));
    }

    let script = null;
    if (prefill?.script_src) {
      script = getParams(decodeURIComponent(prefill.script_src));
    }

    return { location: prefill, parent, script };
  }

  #setPrefillData(query, fullUrl) {
    const prefillParams = { ...query };
    if (prefillParams.parent_url) {
      prefillParams.parent_url = decodeURIComponent(prefillParams.parent_url);
    } else if (fullUrl) {
      prefillParams.parent_url = fullUrl;
    }
    const { location, script, parent } = this.#prefillToObject(prefillParams);

    const instance = useInstanceSettingsStore(this.#pinia);
    instance.parent_url = location.parent_url || null;
    instance.prefill.location = location;
    instance.prefill.parent = parent || {};
    instance.prefill.script = script || {};
  }
}
